import { monitoringConfig } from '../config/monitoring';
import { Observation, ObservationImportance, ObservationSource, ObservationType } from '../types/domain';

export interface ObservationCondition {
  type: ObservationType;
  source: ObservationSource;
  present: boolean;
  message: string;
  importance?: ObservationImportance;
  confidence?: number;
  confirmationMs: number;
  recoveryMs?: number;
  technicalDetails?: Observation['technicalDetails'];
  now?: number;
}

const completed = (item: Observation) => item.status === 'recovered' || item.status === 'unresolved';

export function applyObservationCondition(history: Observation[], input: ObservationCondition): Observation[] {
  const now = input.now ?? Date.now();
  const nowIso = new Date(now).toISOString();
  const currentIndex = history.findIndex((item) =>
    item.type === input.type && item.source === input.source && (item.status === 'pending' || item.status === 'active')
  );

  if (input.present) {
    if (currentIndex >= 0) {
      const current = history[currentIndex];
      const currentOccurrenceDuration = Math.max(0, now - Date.parse(current.startedAt));
      const durationMs = (current.accumulatedDurationMs ?? 0) + currentOccurrenceDuration;
      const updated: Observation = {
        ...current,
        status: currentOccurrenceDuration >= input.confirmationMs ? 'active' : 'pending',
        durationMs,
        confidence: input.confidence ?? current.confidence,
        technicalDetails: { ...current.technicalDetails, ...input.technicalDetails },
        lastUpdatedAt: nowIso,
        recoveryStartedAt: undefined
      };
      return boundObservations(history.map((item, index) => index === currentIndex ? updated : item));
    }

    const mergeIndex = history.findIndex((item) =>
      item.type === input.type && item.source === input.source && completed(item) &&
      now - Date.parse(item.lastUpdatedAt) <= monitoringConfig.timeline.sameEventMergeIntervalMs
    );
    if (mergeIndex >= 0) {
      const previous = history[mergeIndex];
      const reopened: Observation = {
        ...previous,
        status: input.confirmationMs === 0 ? 'active' : 'pending',
        startedAt: nowIso,
        endedAt: undefined,
        durationMs: 0,
        recovered: false,
        occurrenceCount: previous.occurrenceCount + 1,
        lastUpdatedAt: nowIso,
        recoveryStartedAt: undefined,
        confidence: input.confidence,
        technicalDetails: input.technicalDetails ?? {},
        accumulatedDurationMs: previous.durationMs,
        occurrenceDurationsMs: previous.occurrenceDurationsMs
      };
      return boundObservations([reopened, ...history.filter((_, index) => index !== mergeIndex)]);
    }

    const observation: Observation = {
      id: crypto.randomUUID(),
      type: input.type,
      source: input.source,
      status: input.confirmationMs === 0 ? 'active' : 'pending',
      startedAt: nowIso,
      durationMs: 0,
      recovered: false,
      importance: input.importance ?? 'attention',
      confidence: input.confidence,
      message: input.message,
      technicalDetails: input.technicalDetails ?? {},
      occurrenceCount: 1,
      lastUpdatedAt: nowIso
    };
    return boundObservations([observation, ...history]);
  }

  if (currentIndex < 0) return history;
  const current = history[currentIndex];
  if (current.status === 'pending') return history.filter((_, index) => index !== currentIndex);
  const recoveryStartedAt = current.recoveryStartedAt ?? nowIso;
  if (now - Date.parse(recoveryStartedAt) < (input.recoveryMs ?? 0)) {
    return history.map((item, index) => index === currentIndex ? { ...item, recoveryStartedAt, lastUpdatedAt: nowIso } : item);
  }
  const currentOccurrenceDuration = Math.max(0, now - Date.parse(current.startedAt));
  const recovered: Observation = {
    ...current,
    status: 'recovered',
    endedAt: nowIso,
    durationMs: (current.accumulatedDurationMs ?? 0) + currentOccurrenceDuration,
    recovered: true,
    lastUpdatedAt: nowIso,
    recoveryStartedAt: undefined,
    occurrenceDurationsMs: [...(current.occurrenceDurationsMs ?? []), currentOccurrenceDuration]
  };
  return boundObservations(history.map((item, index) => index === currentIndex ? recovered : item));
}

export function finalizeObservations(history: Observation[], now = Date.now()): Observation[] {
  const endedAt = new Date(now).toISOString();
  return boundObservations(history.flatMap((item) => {
    if (item.status === 'pending') return [];
    if (item.status !== 'active') return [item];
    return [{
      ...item,
      status: 'unresolved' as const,
      endedAt,
      durationMs: (item.accumulatedDurationMs ?? 0) + Math.max(0, now - Date.parse(item.startedAt)),
      recovered: false,
      lastUpdatedAt: endedAt,
      recoveryStartedAt: undefined,
      occurrenceDurationsMs: [...(item.occurrenceDurationsMs ?? []), Math.max(0, now - Date.parse(item.startedAt))]
    }];
  }));
}

export function boundObservations(history: Observation[]): Observation[] {
  const active = history.filter((item) => !completed(item)).slice(0, monitoringConfig.timeline.maximumActiveObservations);
  const finished = history.filter(completed).slice(0, monitoringConfig.timeline.maximumCompletedObservations);
  return [...active, ...finished].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
}

export function observationDurationLabel(durationMs: number): string {
  const seconds = Math.max(0, Math.round(durationMs / 1000));
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

export function summarizeCandidatePresence(history: Observation[]) {
  const absences = history.filter((item) => item.type === 'candidate-absent' && item.status !== 'pending');
  const occurrenceDurations = absences.flatMap((item) => item.occurrenceDurationsMs?.length ? item.occurrenceDurationsMs : [item.durationMs]);
  return {
    totalAbsenceCount: absences.reduce((total, item) => total + item.occurrenceCount, 0),
    longestAbsenceMs: occurrenceDurations.length ? Math.max(...occurrenceDurations) : 0,
    totalConfirmedAbsenceMs: absences.reduce((total, item) => total + item.durationMs, 0)
  };
}
